import { createBaseTypeUserState } from '../utils'
import { parseRawAmount } from '../../../utils/parsing'
import { isFailedCall } from '../../../utils/multicall'
import { YldrTypeGetUserReserveIndexes } from '../../../types/lender/aave-v3-types'
import { createMarketUid } from '../../../utils/marketUid'
import {
  LenderCrossPoolMeta,
  LenderYieldComplete,
  UserData,
} from '../utils/types'
import { getDisplayPrice, getOraclePrice } from '../utils/oraclePrice'
import { getAaveAssets } from '../../../assets'

/** Like Aave V3, not stable debt data and no emode */
export const getYldrUserDataConverter = (
  lender: string,
  chainId: string,
  account: string,
  metaMap?: LenderCrossPoolMeta,
): [(data: any[]) => UserData | undefined, number] => {
  // Use same asset ordering as build function
  const assetsToQuery = getAaveAssets(chainId, lender).sort((a, b) =>
    a.toLowerCase().localeCompare(b.toLowerCase()),
  )
  const expectedNumberOfCalls = assetsToQuery.length
  return [
    (data: any[]) => {
      if (data.length !== expectedNumberOfCalls) {
        return undefined
      }
      const lendingPositions: { [asset: string]: any } = {}

      let totalDebt24h = 0
      let totalDeposits24h = 0

      for (let i = 0; i < assetsToQuery.length; i++) {
        const asset = assetsToQuery[i]
        const key = createMarketUid(chainId, lender, asset)
        const metaEntity = metaMap?.[key]!
        const { dataForAsset, addedDebt, addedDeposits } = createAaveV3Entry(
          i,
          data,
          key,
          metaEntity,
        )
        if (!dataForAsset) continue
        totalDebt24h += addedDebt
        totalDeposits24h += addedDeposits
        lendingPositions[key] = dataForAsset
      }

      const payload = {
        chainId,
        account,
        lendingPositions,
        rewards: [],
        userEMode: 0,
      }

      const userData = createBaseTypeUserState(
        payload,
        metaMap!,
        totalDeposits24h,
        totalDebt24h,
      )
      return userData
    },
    expectedNumberOfCalls,
  ]
}

function createAaveV3Entry(
  i: number,
  data: any[],
  key: string,
  meta: LenderYieldComplete,
) {
  const reserveData = data[i]

  // A failed read is not a zero balance — skip the market.
  if (isFailedCall(reserveData)) {
    return { dataForAsset: undefined, addedDeposits: 0, addedDebt: 0 }
  }

  const currentATokenBalanceRaw =
    reserveData[YldrTypeGetUserReserveIndexes.currentYTokenBalance]?.toString()
  const currentVariableDebtRaw =
    reserveData[YldrTypeGetUserReserveIndexes.currentVariableDebt]?.toString()

  if (currentATokenBalanceRaw === '0' && currentVariableDebtRaw === '0') {
    return {
      dataForAsset: undefined,
      addedDeposits: 0,
      addedDebt: 0,
    }
  }

  const assetMeta = meta.asset
  const decimals = assetMeta?.decimals ?? 18

  const currentATokenBalance = parseRawAmount(currentATokenBalanceRaw, decimals)
  const currentVariableDebt = parseRawAmount(currentVariableDebtRaw, decimals)

  // prices
  const price = getDisplayPrice(meta)
  const oPrice = getOraclePrice(meta)
  const priceHist = meta?.price?.priceUsd24h ?? price

  const dataForAsset = {
    marketUid: key,
    underlying: assetMeta.address,
    deposits: currentATokenBalance,
    debtStable: '0',
    debt: currentVariableDebt,
    depositsUSD: Number(currentATokenBalance) * price,
    debtStableUSD: 0,
    debtUSD: Number(currentVariableDebt) * price,
    depositsUSDOracle: Number(currentATokenBalance) * oPrice,
    debtStableUSDOracle: 0,
    debtUSDOracle: Number(currentVariableDebt) * oPrice,
    stableBorrowRate: 0,
    collateralEnabled: Boolean(
      reserveData[YldrTypeGetUserReserveIndexes.usageAsCollateralEnabled],
    ),
    claimableRewards: 0,
  }

  return {
    dataForAsset,
    addedDeposits: Number(currentATokenBalance) * priceHist,
    addedDebt: Number(currentVariableDebt) * priceHist,
  }
}
